import { Grid, Skeleton } from "@mui/material";
import { CopyWithAlert } from "components/CopyWithAlert/CopyWithAlert";
import { ReactComponent as HelpCircleIcon } from "components/Icons/help-circle.svg";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import useCopyWithAlert from "hooks/useCopyWithAlert";
import { useIsOverflown } from "hooks/useIsOverflown";
import { Fragment, ReactNode } from "react";
import { useStyles } from "./BoxSection.styles";

type Props = {
  copy?: boolean;
  dataHjSupress?: boolean;
  dataTestId?: string;
  icon?: JSX.Element;
  label: string;
  tooltip?: JSX.Element;
  value?: string | number | ReactNode;
};

export const BoxLabel = ({
  label,
  dataTestId,
}: {
  dataTestId?: string;
  label: string;
}) => {
  const { observeElement, isOverflown } = useIsOverflown();
  const classes = useStyles();

  return (
    <Tooltip
      className={classes.tooltip}
      disableHoverListener={!isOverflown}
      text={label}
    >
      <div data-test-id={dataTestId} ref={observeElement}>
        {label}
      </div>
    </Tooltip>
  );
};

export const BoxValue = ({
  alertVisibility,
  copy,
  dataTestId,
  value,
}: {
  alertVisibility?: boolean;
  copy?: boolean;
  dataTestId?: string;
  value?: string | ReactNode | number;
}) => {
  const { observeElement, isOverflown } = useIsOverflown();
  const classes = useStyles();

  return (
    <Tooltip
      text={value}
      className={classes.value}
      disableHoverListener={!isOverflown}
    >
      <div>
        <span
          ref={observeElement}
          className={classes.valueText}
          data-test-id={dataTestId}
        >
          {value || "-"}
        </span>
        <CopyWithAlert
          canCopy={!!copy && !!value}
          alertVisibility={!!alertVisibility}
          value={typeof value === "string" ? value : undefined}
        />
      </div>
    </Tooltip>
  );
};

const Box = ({
  className,
  item,
  rowsPerPage = 4,
}: {
  className?: string;
  item: Props;
  rowsPerPage?: number;
}) => {
  const { alertVisibility, handleCopy } = useCopyWithAlert();
  const classes = useStyles();
  const { message } = useMessage();

  return (
    <Grid
      className={`${classes.item} ${item.copy ? classes.clickable : ""} ${className ? className : ""}`}
      item
      key={item.label}
      onClick={() =>
        typeof item.value === "string" && item.copy && handleCopy(item.value)
      }
      xs={12 / rowsPerPage}
    >
      <div className={classes.label}>
        {item.icon && item.icon}
        <BoxLabel label={message({ id: item.label })} />
        {item.tooltip && (
          <Tooltip text={item.tooltip}>
            <HelpCircleIcon className={classes.tooltipIcon} />
          </Tooltip>
        )}
      </div>
      <BoxValue
        copy={item.copy}
        value={item.value}
        dataTestId={item.dataTestId}
        alertVisibility={alertVisibility}
      />
    </Grid>
  );
};

export function BoxSection({
  items,
  rowsPerPage = 4,
}: {
  items: Props[];
  rowsPerPage?: number;
}) {
  const classes = useStyles();

  return (
    <>
      {items && items.length > 0 ? (
        <Grid container rowGap={16} className={classes.container}>
          {items.map((item, index) => {
            const shouldAddDividerBeforeItem =
              index % rowsPerPage === 0 && index - 1 > 0;

            return (
              <Fragment key={index}>
                {shouldAddDividerBeforeItem && (
                  <div className={classes.divider} />
                )}
                <Box
                  item={item}
                  rowsPerPage={rowsPerPage}
                  className={
                    (index + 1) % rowsPerPage === 0 ? classes.noBorder : ""
                  }
                />
              </Fragment>
            );
          })}
        </Grid>
      ) : (
        <Skeleton height={100} />
      )}
    </>
  );
}
